import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  FunctionField,
  Filter,
  SelectInput,
  TopToolbar,
  CreateButton,
  RefreshButton,
  useTranslate,
} from 'react-admin';
import { Box, Chip } from '@mui/material';
import { Star } from '@mui/icons-material';
import { MODEL_ROLES, PROVIDER_LABELS } from './modelConfigFormHelpers';
import type { ModelProvider } from './modelConfigFormHelpers';
import { SetDefaultButton } from './SetDefaultButton';

const ModelConfigFilter = (props: any) => {
  const translate = useTranslate();
  return (
    <Filter {...props}>
      <SelectInput
        source="role"
        label={translate('resources.ai-model-configs.filters.role', { _: 'Role' })}
        choices={MODEL_ROLES.map((r) => ({
          id: r,
          name: translate(`resources.ai-model-configs.roles.${r}`, { _: r }),
        }))}
        alwaysOn
      />
      <SelectInput
        source="isActive"
        label={translate('resources.ai-model-configs.filters.isActive', { _: 'Status' })}
        choices={[
          { id: 'true', name: translate('resources.ai-model-configs.filters.active', { _: 'Active' }) },
          {
            id: 'false',
            name: translate('resources.ai-model-configs.filters.inactive', { _: 'Inactive' }),
          },
        ]}
      />
    </Filter>
  );
};

const ListActions = () => (
  <TopToolbar>
    <CreateButton />
    <RefreshButton />
  </TopToolbar>
);

// Default chip/star: a row that is the current default for its role shows a star chip so the default
// per role is visible at a glance (one-default-per-role UX, REF-01 s.1.1).
const DefaultCell = ({ isDefault }: { isDefault: boolean }) => {
  const translate = useTranslate();
  return isDefault ? (
    <Chip
      size="small"
      color="primary"
      icon={<Star sx={{ fontSize: 16 }} />}
      label={translate('resources.ai-model-configs.fields.default', { _: 'Default' })}
    />
  ) : (
    <Box component="span" sx={{ color: 'text.disabled' }}>
      —
    </Box>
  );
};

// React Admin List for backend_ai_model_configs (sub-plan 20), wired to
// /api/backend/ai-troubleshooting/model-configs via the dataProvider resource map. Columns:
// displayName, provider (friendly label), modelName, role, isActive, isDefault (star), updatedAt,
// plus a "Set as default" row action on non-default rows. No bulk delete.
export const ModelConfigList = () => {
  const translate = useTranslate();
  return (
    <List
      filters={<ModelConfigFilter />}
      actions={<ListActions />}
      empty={false}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      title={translate('resources.ai-model-configs.name', { _: 'AI Model Configs' })}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="displayName" label={translate('resources.ai-model-configs.fields.displayName', { _: 'Display Name' })} />
        <FunctionField
          label={translate('resources.ai-model-configs.fields.provider', { _: 'Provider' })}
          render={(record: any) =>
            PROVIDER_LABELS[record.provider as ModelProvider] ?? record.provider}
        />
        <TextField source="modelName" label={translate('resources.ai-model-configs.fields.modelName', { _: 'Model Name' })} />
        <FunctionField
          label={translate('resources.ai-model-configs.fields.role', { _: 'Role' })}
          render={(record: any) =>
            translate(`resources.ai-model-configs.roles.${record.role}`, { _: record.role })}
        />
        <BooleanField source="isActive" label={translate('resources.ai-model-configs.fields.isActive', { _: 'Active' })} />
        <FunctionField
          label={translate('resources.ai-model-configs.fields.isDefault', { _: 'Default' })}
          render={(record: any) => <DefaultCell isDefault={!!record.isDefault} />}
        />
        <DateField source="updatedAt" showTime label={translate('resources.ai-model-configs.fields.updatedAt', { _: 'Updated' })} />
        <SetDefaultButton />
      </Datagrid>
    </List>
  );
};

export default ModelConfigList;
